/**
 * WHAT TO CALL A MACHINE. One implementation, because there were four.
 *
 * The rule is nickname, else hostname, else a short form of the id — and every part of
 * it has drawn blood:
 *
 *   · `||` and NOT `??`, because the BFF sends `nickname: ''` for a machine nobody has
 *     renamed rather than null. `??` keeps the empty string and the row renders a raw
 *     UUID. Shipped 2026-08-22 in the fork machine picker; caught by looking at the
 *     live list, not by a test.
 *   · TRIMMED BEFORE the fallback chain, not after. `(nickname || hostname || id).trim()`
 *     looks equivalent and is not: a whitespace-only nickname is TRUTHY, so `||` takes
 *     it, `.trim()` empties it, and the last fallback answers. Shipped 2026-09-02, same
 *     picker, same field, different spelling.
 *   · The hostname is trimmed too. Nothing forbids a machine reporting `"  "`, and the
 *     copy that trimmed only the nickname would have rendered blank space as a name.
 *
 * The four copies disagreed about the last resort as well: the rail, the panel and its
 * row showed the first 8 characters of the id, and the fork picker showed the whole
 * 36-character UUID. Eight wins — it is what three of four did, it is what fits a row,
 * and a person can still match it against `avengents status`. That is a deliberate,
 * visible change to the fork picker, not an accident of consolidation.
 *
 * Written to accept a loose shape on purpose: these fields arrive from the roster, from
 * `/account/devices` and from fork targets, each with its own type, and a signature that
 * only one of them satisfies is how a fifth copy gets written.
 */
export function deviceDisplayName(d: {
  nickname?: string | null
  hostname?: string | null
  device_id?: string | null
}): string {
  const nickname = (d.nickname ?? '').trim()
  if (nickname) return nickname
  const hostname = (d.hostname ?? '').trim()
  if (hostname) return hostname
  const id = (d.device_id ?? '').trim()
  // 'unnamed' rather than an empty cell: a row with no text at all reads as a rendering
  // fault, and this case (no id either) means the roster row itself is malformed.
  return id ? id.slice(0, 8) : 'unnamed'
}
